import { useEffect, useState } from 'react';
import { getSocket } from '@/net/socket';
import { EV } from '@shared/protocol';

const PING_INTERVAL_MS = 3000;

export interface PingState {
  /** Round-trip latency in ms, or null until the first pong lands. */
  rtt: number | null;
  /** False while the socket is disconnected. */
  online: boolean;
}

/**
 * Measures live latency over the gameplay socket by echoing a timestamp
 * (net.ping → net.pong) every few seconds. This is the path that actually
 * carries taps/skills, so it reflects in-match responsiveness rather than a
 * plain HTTP probe.
 */
export function usePing(): PingState {
  const [rtt, setRtt] = useState<number | null>(null);
  const [online, setOnline] = useState(false);

  useEffect(() => {
    const socket = getSocket();
    let timer: ReturnType<typeof setInterval> | null = null;

    const onPong = (p: { t: number }) => setRtt(Math.max(0, Math.round(Date.now() - p.t)));
    const send = () => {
      if (socket.connected) socket.emit(EV.NET_PING, { t: Date.now() });
    };
    const onConnect = () => { setOnline(true); send(); };
    const onDisconnect = () => { setOnline(false); setRtt(null); };

    socket.on(EV.NET_PONG, onPong);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    setOnline(socket.connected);
    send();
    timer = setInterval(send, PING_INTERVAL_MS);

    return () => {
      if (timer) clearInterval(timer);
      socket.off(EV.NET_PONG, onPong);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
    };
  }, []);

  return { rtt, online };
}
